import { useEffect, useState } from "react";
import { getDocs, collection } from "firebase/firestore";
import { db } from "./firebase";
import { createWeeklyQuestPrompt, deleteWeeklyQuestPrompt, updateWeeklyQuestPrompt } from "./weeklyQuests";
import RichTextEditor, { RichTextDisplay } from "./RichTextEditor";

export default function AdminWeeklyPrompts({ currentUser, programOptions = [] }) {
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [tag, setTag] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [xpReward, setXpReward] = useState(20);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [status, setStatus] = useState("");

  const [allPrompts, setAllPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null); // { id, title, prompt, tag, startDate, endDate, xpReward, commentsEnabled }
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!tag && programOptions.length > 0) {
      setTag(programOptions[0].value);
    }
  }, [programOptions, tag]);

  useEffect(() => {
    loadAllPrompts();
  }, []);

  async function loadAllPrompts() {
    setPromptsLoading(true);
    try {
      const snap = await getDocs(collection(db, "weeklyQuestPrompts"));
      const prompts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      prompts.sort((a, b) => {
        const aTime = a.startAt?.toDate?.()?.getTime?.() || 0;
        const bTime = b.startAt?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });
      setAllPrompts(prompts);
    } catch (err) {
      console.error("Error loading prompts:", err);
    } finally {
      setPromptsLoading(false);
    }
  }

  function toLocalDatetimeValue(ts) {
    const d = ts?.toDate?.();
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function openEdit(p) {
    setEditingPrompt({
      id: p.id,
      title: p.title || "",
      prompt: p.prompt || "",
      tag: p.tag || "",
      startDate: toLocalDatetimeValue(p.startAt),
      endDate: toLocalDatetimeValue(p.endAt),
      xpReward: p.xpReward ?? 20,
      commentsEnabled: p.commentsEnabled ?? true,
    });
  }

  async function handleEditSave() {
    if (!editingPrompt) return;
    setEditSaving(true);
    try {
      await updateWeeklyQuestPrompt(editingPrompt.id, editingPrompt);
      setAllPrompts((prev) =>
        prev.map((p) =>
          p.id === editingPrompt.id
            ? { ...p, ...editingPrompt, startAt: { toDate: () => new Date(editingPrompt.startDate) }, endAt: { toDate: () => new Date(editingPrompt.endDate) } }
            : p
        )
      );
      setEditingPrompt(null);
    } catch (err) {
      console.error("Error updating prompt:", err);
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeletePrompt(promptId) {
    setDeletingId(promptId);
    try {
      await deleteWeeklyQuestPrompt(promptId);
      setAllPrompts((prev) => prev.filter((p) => p.id !== promptId));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Error deleting prompt:", err);
    } finally {
      setDeletingId(null);
    }
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "2px solid #bfdbfe",
    background: "#f5faff",
    color: "#1e3a5f",
    fontSize: "15px",
    outline: "none",
  };

  const labelStyle = {
    display: "grid",
    gap: "6px",
    color: "#4b6b8a",
    fontSize: "12px",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };

  async function handleCreatePrompt(e) {
    e.preventDefault();

    try {
      setStatus("Saving...");

      await createWeeklyQuestPrompt({
        title,
        prompt,
        tag,
        startDate,
        endDate,
        xpReward,
        commentsEnabled,
        adminUid: currentUser.uid,
      });

      setTitle("");
      setPrompt("");
      setTag(programOptions[0]?.value || "");
      setStartDate("");
      setEndDate("");
      setXpReward(20);
      setCommentsEnabled(true);

      setStatus("Weekly Quest Prompt created!");
      await loadAllPrompts();
    } catch (error) {
      console.error(error);
      setStatus("Error creating prompt");
    }
  }

  function formatDate(ts) {
    const d = ts?.toDate?.();
    if (!d) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  }

  function getPromptStatus(p) {
    const now = new Date();
    const start = p.startAt?.toDate?.();
    const end = p.endAt?.toDate?.();
    if (!start || !end) return { label: "Unknown", color: "#9ca3af" };
    if (now < start) return { label: "Upcoming", color: "#f59e0b" };
    if (now > end) return { label: "Ended", color: "#6b7280" };
    return { label: "Active", color: "#22c55e" };
  }

  return (
    <div style={{ display: "grid", gap: "20px" }}>
      {/* Create form */}
      <div
        style={{
          background: "#f8fbff",
          borderRadius: "18px",
          padding: "18px",
          border: "2px solid #bfdbfe",
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: "6px", color: "#1e3a5f" }}>Weekly Quest Prompts</h2>
        <p style={{ marginTop: 0, marginBottom: "14px", color: "#4b6b8a", fontSize: "14px" }}>
          Send a weekly assignment to a specific program.
        </p>

        <form onSubmit={handleCreatePrompt} style={{ display: "grid", gap: 12, maxWidth: 680 }}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Quest title"
            style={inputStyle}
            required
          />

          <RichTextEditor
            value={prompt}
            onChange={setPrompt}
            placeholder="Quest prompt"
            rows={6}
          />

          <label style={labelStyle}>
            Program
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              style={inputStyle}
              required
            >
              {programOptions.map((program) => (
                <option key={program.value} value={program.value}>
                  {program.label}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            Start date and time
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            End date and time
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
              required
            />
          </label>

          <label style={labelStyle}>
            XP reward
            <input
              type="number"
              value={xpReward}
              onChange={(e) => setXpReward(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px" }}>
            <input
              type="checkbox"
              checked={commentsEnabled}
              onChange={(e) => setCommentsEnabled(e.target.checked)}
            />
            Comments enabled
          </label>

          <button
            type="submit"
            style={{
              padding: "12px 16px",
              borderRadius: "14px",
              border: "none",
              background: "#38bdf8",
              color: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 6px 16px rgba(159, 122, 234, 0.4)",
            }}
          >
            Create Weekly Quest Prompt
          </button>
        </form>

        {status ? <p style={{ marginTop: "12px", color: "#4b6b8a", fontWeight: "bold" }}>{status}</p> : null}
      </div>

      {/* Existing prompts list */}
      <div
        style={{
          background: "#f8fbff",
          borderRadius: "18px",
          padding: "18px",
          border: "2px solid #bfdbfe",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
          <h3 style={{ margin: 0, color: "#1e3a5f", fontSize: "18px" }}>All Prompts</h3>
          <button
            type="button"
            onClick={loadAllPrompts}
            style={{ padding: "6px 12px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
          >
            Refresh
          </button>
        </div>

        {promptsLoading ? (
          <div style={{ color: "#4b6b8a", fontSize: "14px" }}>Loading...</div>
        ) : allPrompts.length === 0 ? (
          <div style={{ color: "#4b6b8a", fontSize: "14px" }}>No prompts yet.</div>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {allPrompts.map((p) => {
              const { label: statusLabel, color: statusColor } = getPromptStatus(p);
              const isConfirming = confirmDeleteId === p.id;
              const isDeleting = deletingId === p.id;

              return (
                <div
                  key={p.id}
                  style={{
                    background: "#f5faff",
                    borderRadius: "14px",
                    padding: "14px 16px",
                    border: "2px solid #dbeafe",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                        <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{p.title}</span>
                        <span style={{ fontSize: "11px", fontWeight: "bold", color: statusColor, background: `${statusColor}18`, padding: "2px 8px", borderRadius: "999px", border: `1px solid ${statusColor}44` }}>
                          {statusLabel}
                        </span>
                      </div>
                      <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "4px" }}>
                        {p.tag} · {formatDate(p.startAt)} → {formatDate(p.endAt)}
                      </div>
                      <div style={{ color: "#6b8cb0", fontSize: "13px", lineHeight: 1.4, maxHeight: "60px", overflow: "hidden" }}>
                        <RichTextDisplay html={p.prompt} />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        style={{ padding: "6px 12px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                      >
                        ✏️ Edit
                      </button>
                      {!isConfirming ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(p.id)}
                          style={{ padding: "6px 12px", borderRadius: "10px", border: "2px solid #fca5a5", background: "#fff1f2", color: "#dc2626", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                        >
                          Delete
                        </button>
                      ) : (
                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          <span style={{ fontSize: "12px", color: "#dc2626", fontWeight: "bold" }}>Sure?</span>
                          <button
                            type="button"
                            onClick={() => handleDeletePrompt(p.id)}
                            disabled={isDeleting}
                            style={{ padding: "6px 12px", borderRadius: "10px", border: "none", background: "#dc2626", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: isDeleting ? "not-allowed" : "pointer", opacity: isDeleting ? 0.6 : 1 }}
                          >
                            {isDeleting ? "Deleting..." : "Yes, delete"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            style={{ padding: "6px 12px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {editingPrompt?.id === p.id && (
                    <div style={{ marginTop: "14px", display: "grid", gap: "10px", borderTop: "2px solid #bfdbfe", paddingTop: "14px" }}>
                      <input value={editingPrompt.title} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, title: e.target.value }))} placeholder="Quest title" style={inputStyle} />
                      <RichTextEditor value={editingPrompt.prompt} onChange={(v) => setEditingPrompt((prev) => ({ ...prev, prompt: v }))} placeholder="Quest prompt" rows={5} />
                      <label style={labelStyle}>
                        Program
                        <select value={editingPrompt.tag} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, tag: e.target.value }))} style={inputStyle}>
                          {programOptions.map((prog) => <option key={prog.value} value={prog.value}>{prog.label}</option>)}
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Start date and time
                        <input type="datetime-local" value={editingPrompt.startDate} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, startDate: e.target.value }))} style={inputStyle} />
                      </label>
                      <label style={labelStyle}>
                        End date and time
                        <input type="datetime-local" value={editingPrompt.endDate} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, endDate: e.target.value }))} style={inputStyle} />
                      </label>
                      <label style={labelStyle}>
                        XP reward
                        <input type="number" value={editingPrompt.xpReward} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, xpReward: e.target.value }))} style={inputStyle} />
                      </label>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px" }}>
                        <input type="checkbox" checked={editingPrompt.commentsEnabled} onChange={(e) => setEditingPrompt((prev) => ({ ...prev, commentsEnabled: e.target.checked }))} />
                        Comments enabled
                      </label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button type="button" onClick={handleEditSave} disabled={editSaving} style={{ padding: "10px 18px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: editSaving ? "not-allowed" : "pointer", opacity: editSaving ? 0.6 : 1 }}>
                          {editSaving ? "Saving..." : "Save changes"}
                        </button>
                        <button type="button" onClick={() => setEditingPrompt(null)} style={{ padding: "10px 18px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
